import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { getDisplayNameForUI } from '../../../utils/displayName';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Menu, 
  Bell, 
  User as UserIcon, 
  ChevronDown, 
  LogOut,
  Settings as SettingsIcon,
  Search,
  LayoutGrid
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './Sidebar';
import Overview from './pages/Overview';
import Wishlist from './pages/Wishlist';
import Orders from './pages/Orders';
import Invoices from './pages/Invoices';
import Jobs from './pages/Jobs';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import VehicleManager from '../../Vehicles/VehicleManager';
import Deals from '../admin/pages/Deals';

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/customer/dashboard' || path === '/customer/dashboard/') return 'overview';
    if (path.includes('/wishlist')) return 'wishlist';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/invoices')) return 'invoices';
    if (path.includes('/jobs')) return 'jobs';
    if (path.includes('/deals')) return 'deals';
    if (path.includes('/vehicles')) return 'vehicles';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/settings')) return 'settings';
    return 'overview';
  };

  const currentPage = getCurrentPage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    
    const handleClickOutside = (e) => {
      if (showProfileMenu && !e.target.closest('.profile-menu')) setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const pageTitles = {
    overview: 'Dashboard Overview',
    wishlist: 'My Wishlist',
    orders: 'Order History',
    invoices: 'My Invoices',
    jobs: 'Service Center',
    deals: 'Deals',
    vehicles: 'Garage Manager',
    profile: 'My Profile',
    settings: 'Account Settings'
  };

  return (
    <div className="sp-dashboard-premium sp-dashboard-dark h-screen flex overflow-hidden">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden relative">
        {/* Dynamic Background Elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-500/5 blur-[120px] rounded-full -mr-64 -mt-64 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-500/5 blur-[120px] rounded-full -ml-64 -mb-64 pointer-events-none" />

        <header className={`z-40 transition-all duration-300 ${scrolled ? 'bg-slate-900/85 backdrop-blur-lg shadow-soft py-2 border-b border-slate-700/60' : 'bg-transparent py-4'}`}>
          <div className="px-4 md:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden w-10 h-10 flex items-center justify-center rounded-xl text-slate-300 hover:bg-slate-800 hover:shadow-sm transition-all"
                >
                  <Menu size={20} />
                </button>
                <div className="flex items-center gap-4">
                  <img 
                    src="/sparepe.jpeg" 
                    alt="SparePe Logo" 
                    className="hidden sm:block w-10 h-10 rounded-xl object-cover bg-white shadow-sm border border-slate-100" 
                  />
                  <div>
                    <motion.h1 
                      key={currentPage}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="text-xl md:text-2xl font-black text-slate-100 tracking-tight"
                    >
                      {pageTitles[currentPage] || 'My Dashboard'}
                    </motion.h1>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 md:gap-4">
                <div className="hidden md:flex items-center bg-slate-800/70 backdrop-blur-sm border border-slate-600 rounded-xl px-3 py-1.5 focus-within:ring-2 focus-within:ring-primary-500/20 transition-all">
                  <Search size={16} className="text-slate-400" />
                  <input type="text" placeholder="Search..." className="bg-transparent border-none focus:ring-0 text-sm w-40 text-slate-100 placeholder:text-slate-400" />
                </div>

                <button
                  type="button"
                  className="relative w-10 h-10 flex items-center justify-center rounded-xl text-slate-300 hover:bg-slate-800 hover:shadow-sm hover:text-primary-400 transition-all"
                >
                  <Bell size={20} />
                  <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-rose-500 rounded-full border-2 border-white ring-1 ring-rose-500/50" />
                </button>

                <div className="relative profile-menu">
                  <button
                    type="button"
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-2 p-1 pr-3 rounded-2xl hover:bg-slate-800 hover:shadow-soft transition-all border border-transparent hover:border-slate-700"
                  >
                    <div className="relative">
                      {user?.avatar && !avatarError ? (
                        <img loading="lazy"
                          src={user.avatar}
                          alt="Profile"
                          className="w-8 h-8 rounded-xl object-cover border border-slate-200"
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-xl bg-slate-900 flex items-center justify-center text-white font-bold text-xs shadow-inner">
                          {(getDisplayNameForUI(user) || 'C').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />
                    </div>
                    <span className="hidden md:inline text-xs font-black text-slate-300 tracking-wide uppercase">
                      {getDisplayNameForUI(user) || '—'}
                    </span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform duration-300 ${showProfileMenu ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {showProfileMenu && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute right-0 mt-3 w-64 bg-white rounded-3xl shadow-2xl border border-slate-100 z-50 overflow-hidden ring-1 ring-slate-900/5"
                      >
                        <div className="p-5 border-b border-slate-50 bg-slate-50/50">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center text-white font-black text-lg">
                              {(getDisplayNameForUI(user) || 'C').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-black text-slate-900 text-sm tracking-tight">
                                {getDisplayNameForUI(user) || '—'}
                              </p>
                              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{user?.role || 'customer'}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-2">
                          <button
                            type="button"
                            onClick={() => { navigate('/customer/dashboard/profile'); setShowProfileMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all text-xs font-bold uppercase tracking-wider"
                          >
                            <UserIcon size={16} />
                            <span>My Profile</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => { navigate('/customer/dashboard/settings'); setShowProfileMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all text-xs font-bold uppercase tracking-wider"
                          >
                            <SettingsIcon size={16} />
                            <span>Preferences</span>
                          </button>
                          <div className="my-2 border-t border-slate-50" />
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-rose-600 hover:bg-rose-50 transition-all text-xs font-black uppercase tracking-widest"
                          >
                            <LogOut size={16} />
                            <span>Secure Logout</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto relative scrollbar-hide">
          <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentPage}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                className="min-h-full"
              >
                {currentPage === 'overview' && <Overview />}
                {currentPage === 'wishlist' && <Wishlist />}
                {currentPage === 'orders' && <Orders />}
                {currentPage === 'invoices' && <Invoices />}
                {currentPage === 'jobs' && <Jobs />}
                {currentPage === 'deals' && <Deals />}
                {currentPage === 'vehicles' && <VehicleManager />}
                {currentPage === 'profile' && <Profile />}
                {currentPage === 'settings' && <Settings />}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CustomerDashboard;
